// Journal vocabulary shared by the server (parsing) and the client (labels), from the
// Android app's JournalEntry.kt and RunData.kt. Keys are what Nightscout stores.

export const MEALS = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
} as const;
export type Meal = keyof typeof MEALS;

export const ACTIVITY_TYPES = {
  walk: { label: "Walk", emoji: "🚶" },
  run: { label: "Run", emoji: "🏃" },
  bike: { label: "Bike", emoji: "🚴" },
  gym: { label: "Gym", emoji: "🏋️" },
  swim: { label: "Swim", emoji: "🏊" },
  yoga: { label: "Yoga", emoji: "🧘" },
  sport: { label: "Sport", emoji: "⚽" },
  other: { label: "Other", emoji: "🏅" },
} as const;
export type ActivityType = keyof typeof ACTIVITY_TYPES;

export const INTENSITIES = {
  light: "Light",
  moderate: "Moderate",
  hard: "Hard",
} as const;
export type Intensity = keyof typeof INTENSITIES;

export const FOOD_EMOJI = "🍽️";
export const NOTE_EMOJI = "📝";

export function isMeal(key: string | null): key is Meal {
  return key !== null && Object.hasOwn(MEALS, key);
}

/** The meal a food entry gets from the local hour it's at, as on the phone (Meal.forHour). */
export function mealForHour(hour: number): Meal {
  if (hour >= 5 && hour <= 10) return "breakfast";
  if (hour >= 11 && hour <= 14) return "lunch";
  if (hour >= 17 && hour <= 21) return "dinner";
  return "snack";
}

export function isActivityType(key: string | null): key is ActivityType {
  return key !== null && Object.hasOwn(ACTIVITY_TYPES, key);
}

export function isIntensity(key: string | null): key is Intensity {
  return key !== null && Object.hasOwn(INTENSITIES, key);
}

/** Average pace over moving time (elapsed time if moving time is unknown); null under 50 m. */
export function paceSecPerKm(distanceM: number, durationS: number, movingS: number | null): number | null {
  return distanceM >= 50 ? Math.round((movingS ?? durationS) / (distanceM / 1000)) : null;
}

/** Metric formatting for runs, as on the phone (RunFormat). */
export const RunFormat = {
  distance(meters: number): string {
    return `${roundHalfUp(meters / 1000, 1).toFixed(1)} km`;
  },
  pace(secPerKm: number): string {
    return `${Math.trunc(secPerKm / 60)}:${String(secPerKm % 60).padStart(2, "0")}/km`;
  },
  duration(seconds: number): string {
    const minutes = Math.trunc((seconds + 30) / 60);
    return minutes < 60 ? `${minutes} min` : `${Math.trunc(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min`;
  },
};

/**
 * Rounds half away from zero on the decimal digits a person reads, like Java's `%.1f`:
 * `toFixed` works on the binary value, so 1.15 would become "1.1".
 */
export function roundHalfUp(value: number, digits: number): number {
  const factor = 10 ** digits;
  const scaled = Number((Math.abs(value) * factor).toPrecision(15));
  return (Math.sign(value) * Math.round(scaled)) / factor;
}
